import React, { memo } from "react";
import { Box, Stack, Typography, Grid, Container } from "@mui/material";
import { useGetInsuranceOverviewQuery } from "../../store/api/insurance.slice";
import FinancialGoalsIcon from "../../assets/FinFactor/FinancialGoals.svg";
import FinFactorFundCard from "../../components/FinFactorInvestment/FinFactorFundCard";

/* ==============================
   CATEGORY PANEL
============================== */

const InsuranceCategoryPanel = ({ policyGroup }) => {

    return (

        <Box
            sx={{
                mb: 3,
                border: "1px solid rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
                overflow: "hidden",
                backgroundColor: "#1F2335"
            }}
        >

            {/* HEADER */}

            <Box
                sx={{
                    backgroundColor: "#1F2335",
                    px: { xs: 2.5, md: 3 },
                    py: 2.5,
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: "space-between",
                    gap: { xs: 2, md: 0 },
                    borderBottom: "1px solid rgba(255, 255, 255, 0.08)"
                }}
            >

                {/* LEFT */}

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <img
                        src={FinancialGoalsIcon}
                        alt="Icon"
                        width={24}
                        height={24}
                    />

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "flex-start", sm: "baseline" }}>
                        <Typography
                            variant="h6"
                            fontWeight="700"
                            color="white"
                        >
                            {policyGroup.category}
                        </Typography>

                        {policyGroup.asOfDate && (
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: "rgba(255,255,255,0.4)",
                                    fontWeight: 500
                                }}
                            >
                                {policyGroup.asOfDate}
                            </Typography>
                        )}
                    </Stack>

                </Stack>


                {/* RIGHT */}

                <Stack
                    direction="row"
                    spacing={{ xs: 2, md: 6 }}
                    alignItems="center"
                    sx={{
                        width: { xs: "100%", md: "auto" },
                        justifyContent: { xs: "space-between", md: "flex-end" }
                    }}
                >

                    <Box textAlign="right">

                        <Typography
                            color="rgba(255,255,255,0.6)"
                            fontSize="12px"
                            fontWeight={500}
                            display="block"
                            mb={0.5}
                        >
                            Total sum assured
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: "16px", md: "20px" },
                                fontWeight: "700",
                                color: "white",
                                lineHeight: 1
                            }}
                        >
                            {policyGroup.totalSumAssured}
                        </Typography>

                    </Box>


                    <Box sx={{ textAlign: { xs: "left", md: "right" } }}>

                        <Typography
                            color="rgba(255,255,255,0.6)"
                            fontSize="11px"
                            fontWeight={500}
                            display="block"
                            mb={0.5}
                        >
                            Total Premium Value
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: "16px", md: "20px" },
                                fontWeight: "700",
                                color: "white",
                                lineHeight: 1
                            }}
                        >
                            {policyGroup.totalPremium}
                        </Typography>

                    </Box>

                </Stack>

            </Box>



            {/* BODY */}

            <Box
                sx={{
                    px: { xs: 1.5, md: 3 },
                    py: 3,
                    backgroundColor: "rgba(255, 255, 255, 0.01)",
                    maxHeight: policyGroup.items.length > 3 ? 460 : "auto",
                    overflowY: policyGroup.items.length > 3 ? "auto" : "visible",

                    '&::-webkit-scrollbar': {
                        width: 6
                    },

                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(255,255,255,0.02)',
                        borderRadius: 4
                    },

                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,255,255,0.15)',
                        borderRadius: 4,

                        '&:hover': {
                            background: 'rgba(255,255,255,0.25)'
                        }
                    }

                }}
            >

                {policyGroup.items.map((item) => (
                    <FinFactorFundCard
                        key={item.id}
                        {...item}
                        isDynamicGridSize={['Life Insurance'].includes(policyGroup.category)}
                    />
                ))}

            </Box>

        </Box>
    );
};



import NodisplayComponent from "../../components/common/NodisplayComponent";

const FinFactorInsuranceContainer = () => {
    const { data, isLoading, isError } = useGetInsuranceOverviewQuery();
    const { policies = [] } = data || {};

    if (isLoading) return null;

    return (
        <Box sx={{ minHeight: "100vh", pb: 4, pt: { xs: 1.5, md: 3 } }}>
            <Container maxWidth="lg" sx={{ px: { xs: 1.5, sm: 2, md: 0 } }} disableGutters>
                {isError || policies.length === 0 ? (
                    <NodisplayComponent
                        title={isError ? "Error Loading Insurance" : "No Insurance Found"}
                        subtitle={isError ? "We couldn't retrieve your insurance policies at this time. Please try again later." : "No insurance policies connected yet."}
                    />
                ) : (
                    policies.map((policyGroup) => (
                        <InsuranceCategoryPanel
                            key={policyGroup.category}
                            policyGroup={policyGroup}
                        />
                    ))
                )}
            </Container>
        </Box>
    );
};

export default FinFactorInsuranceContainer;